import { useHandler } from "@redotech/react-util/hook";
import {
  rcoProductMetafieldsSegmentSchema,
  RcoProductMetafieldsSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { SegmentListMultipleItemMembershipOperator } from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { OperatorSelector } from "@redotech/redo-web/conversion/operator-selector";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";
import { ProductMetafieldsSelector } from "../../common/product-metafields-selector";

export const ProductMetafieldsInspectorContent = memo(
  function ProductMetafieldsInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const segment = useMemo(
      () => rcoProductMetafieldsSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {operatorsToText(segment.cartOperator, segment.operator)}
          </Text>
        </Flex>
        <Flex dir="column" gap="md" w="full">
          {segment.metafields.map((metafield, index) => (
            <Flex dir="row" gap="sm" key={index} wrap="wrap">
              <RedoBadge color="blue" size="sm" text={metafield.resourceType} />
              <RedoBadge
                color="gray"
                size="sm"
                text={`${metafield.namespace}.${metafield.key}`}
              />
              <RedoBadge
                color="gray"
                size="sm"
                text={`${metafield.value}${metafield.valueMatchKind === "partial" ? " (contains)" : ""}`}
              />
            </Flex>
          ))}
        </Flex>
      </>
    );
  },
);

const operatorsToText = (
  cartOperator: RcoProductMetafieldsSegmentSchema["cartOperator"],
  operator: RcoProductMetafieldsSegmentSchema["operator"],
) => {
  switch (cartOperator) {
    case SegmentListMultipleItemMembershipOperator.ANY_IN:
      switch (operator) {
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return "Any item in the cart has all of the metafields in the list:";
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return "Any item in the cart has at least one metafield in the list:";
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return "Any item in the cart does not have any metafield in the list:";
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.ALL_IN:
      switch (operator) {
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return "All products/variants in the cart have all of the metafields in the list:";
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return "All products/variants in the cart have at least one metafield in the list:";
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return "All products/variants in the cart do not have any metafield in the list";
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.NONE_IN:
      switch (operator) {
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return "No item in the cart has all of the metafields in the list:";
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return "No item in the cart has at least one metafield in the list:";
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return "No item in the cart does not have any metafield in the list:";
        default:
          assertNever(operator);
      }
      break;
    default:
      assertNever(cartOperator);
  }
};

export const segmentListMultipleItemMembershipOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "Any",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "No",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "Every",
};

export const productMetafieldsSegmentListMultipleItemMembershipOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "every",
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "any",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "no",
};

export const EditableProductMetafieldsInspectorContent = memo(
  function EditableProductMetafieldsInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const segment = useMemo(
      () => rcoProductMetafieldsSegmentSchema.parse(step.segment),
      [step],
    );

    const handleCartOperatorSelected = useHandler(
      (operator: SegmentListMultipleItemMembershipOperator) => {
        setStep({ ...step, segment: { ...segment, cartOperator: operator } });
      },
    );

    const handleOperatorSelected = useHandler(
      (operator: SegmentListMultipleItemMembershipOperator) => {
        setStep({ ...step, segment: { ...segment, operator } });
      },
    );

    const handleMetafieldsChanged = useHandler(
      (metafields: RcoProductMetafieldsSegmentSchema["metafields"]) => {
        const updatedSegment = { ...segment, metafields };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Flex>
            <OperatorSelector<SegmentListMultipleItemMembershipOperator>
              availableOptions={Object.values(
                SegmentListMultipleItemMembershipOperator,
              )}
              onOperatorSelected={handleCartOperatorSelected}
              operatorToText={segmentListMultipleItemMembershipOperatorToText}
              selectedOperator={segment.cartOperator}
            />
          </Flex>
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          item in the cart has
        </Text>
        <Flex>
          <OperatorSelector<SegmentListMultipleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListMultipleItemMembershipOperator,
            )}
            onOperatorSelected={handleOperatorSelected}
            operatorToText={
              productMetafieldsSegmentListMultipleItemMembershipOperatorToText
            }
            selectedOperator={segment.operator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          metafield in the list:
        </Text>
        <ProductMetafieldsSelector
          metafields={segment.metafields}
          onMetafieldsChanged={handleMetafieldsChanged}
        />
      </>
    );
  },
);
